import { Button, Combobox, Item, ItemHeader, ItemTitle, Tooltip } from '@ibsys/kit';
import { ExternalLinkIcon, XIcon } from 'lucide-react';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { KnowledgeSetListItem } from '@/entities/knowledge-set';
import { fetchKnowledgeSet, useUpdateKnowledgeSet } from '@/entities/knowledge-set';
import { knowledgeKeys } from '@/entities/knowledge';
import { useSearchQuery } from '@/entities/search';
import type { KnowledgeSetLink } from '@/entities/knowledge';

type KnowledgeNodeSetsProps = {
  nodeId: number;
  knowledgeSets: KnowledgeSetLink[];
};

export const KnowledgeNodeSets: FC<KnowledgeNodeSetsProps> = ({ nodeId, knowledgeSets }) => {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: updateKnowledgeSet, isPending } = useUpdateKnowledgeSet();

  const { data: searchData } = useSearchQuery<KnowledgeSetListItem>({
    entityType: 'knowledge_set',
    filters: { isDeleted: false },
    limit: 200,
    offset: 0,
  });

  const linkedIds = useMemo(() => new Set(knowledgeSets.map((ks) => ks.id)), [knowledgeSets]);

  const comboboxOptions = useMemo(() => {
    const items = searchData?.items ?? [];
    return items.filter((item) => !linkedIds.has(item.id)).map((item) => ({ value: item.id, label: item.name }));
  }, [searchData, linkedIds]);

  const addToSet = useCallback(
    async (setId: number) => {
      const knowledgeSet = await fetchKnowledgeSet(setId);
      const newNodeIds = [...knowledgeSet.nodeIds, nodeId];
      await updateKnowledgeSet({
        id: setId,
        payload: { name: knowledgeSet.name, description: knowledgeSet.description, nodeIds: newNodeIds },
      });
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.details() });
    },
    [nodeId, updateKnowledgeSet, queryClient],
  );

  const removeFromSet = useCallback(
    async (setId: number) => {
      const knowledgeSet = await fetchKnowledgeSet(setId);
      const newNodeIds = knowledgeSet.nodeIds.filter((id) => id !== nodeId);
      await updateKnowledgeSet({
        id: setId,
        payload: { name: knowledgeSet.name, description: knowledgeSet.description, nodeIds: newNodeIds },
      });
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.details() });
    },
    [nodeId, updateKnowledgeSet, queryClient],
  );

  const handleAdd = useCallback(
    (value?: number | string) => {
      if (value == null) return;
      const setId = Number(value);
      const item = searchData?.items.find((i) => i.id === setId);
      if (!item) return;
      addToSet(setId);
    },
    [searchData, addToSet],
  );

  const handleNavigate = useCallback(
    (setId: number) => {
      navigate(`/knowledge-sets/${setId}`);
    },
    [navigate],
  );

  const hasKnowledgeSets = knowledgeSets.length > 0;

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-base font-semibold text-foreground">{t('knowledgeSets')}</h3>

      {hasKnowledgeSets && (
        <div className="flex flex-col gap-1">
          {knowledgeSets.map((ks) => (
            <Item key={ks.id} variant="outline" size="sm">
              <ItemHeader>
                <ItemTitle className="text-sm truncate">{ks.name}</ItemTitle>
                <div className="flex gap-1">
                  <Tooltip tooltip={t('goToKnowledgeSet')}>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleNavigate(ks.id)}
                      aria-label={t('goToKnowledgeSet')}
                    >
                      <ExternalLinkIcon className="size-3.5" />
                    </Button>
                  </Tooltip>
                  <Tooltip tooltip={t('removeKnowledgeSet')}>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFromSet(ks.id)}
                      disabled={isPending}
                      aria-label={t('removeKnowledgeSet')}
                    >
                      <XIcon className="size-3.5" />
                    </Button>
                  </Tooltip>
                </div>
              </ItemHeader>
            </Item>
          ))}
        </div>
      )}

      {!hasKnowledgeSets && <p className="text-sm text-muted-foreground">{t('noKnowledgeSets')}</p>}

      {comboboxOptions.length > 0 && (
        <Combobox
          options={comboboxOptions}
          onChange={handleAdd}
          disabled={isPending}
          placeholder={t('addKnowledgeSet')}
        />
      )}
    </div>
  );
};
